import React from 'react';

import { Button, HStack, Heading, Icon, Stack, Td, Text, Tr, useColorModeValue } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiRuler } from 'react-icons/bi';
import { IoChevronForwardOutline, IoCheckmarkCircle } from 'react-icons/io5';
import { Link, useLocation } from 'react-router';

import { CompareDataset, useCompare, useIsDatasetComparisonEnabled, useRouteDatasetRef } from 'components/shared/Compare';
import CopyToClipboardButton from 'components/shared/CopyToClipboardButton';
import { AccessResourceKey } from 'hooks/data/files/types';
import { ActivityDataset, ActivityType } from 'hooks/data/search/types';
import useSearchFilters from 'hooks/data/search/useSearchFilters';
import useAccess from 'hooks/useAccess';
import useVersionInfo from 'hooks/useVersionInfo';
import { newerVersions } from 'pages/Search/utils';
import { checkAccessToResource } from 'utilities/auth/access';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { formatNumericalValue } from 'utilities/numbers';
import { saveSearchOrigin } from 'utilities/search/searchOrigin';

type Props = {
  dataset: ActivityDataset;
  isLastGroup?: boolean;
  index: number;
  referenceProductName: string;
  unit: string;
  currentlyDisplayed: number;
  resultPosition: number;
  activityName: string;
  activityType: ActivityType;
  sectors: string[];
  timePeriod: string;
  groupDatasetIds: number[];
};

const DatasetRow = ({
  dataset,
  unit,
  isLastGroup,
  index,
  referenceProductName,
  currentlyDisplayed,
  resultPosition,
  activityName,
  activityType,
  sectors,
  timePeriod,
  groupDatasetIds,
}: Props) => {
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const { ver, system_model } = useVersionInfo();
  const { search } = useLocation();
  const { data: accessData } = useAccess();
  const hasScoresAccess = checkAccessToResource(accessData, AccessResourceKey.SCORES, ver);
  const {
    filterValues: { query, ...otherFilters },
  } = useSearchFilters();
  const isFirstDataset = index === 0;
  const grayText = useColorModeValue('gray.700', 'gray.400');
  const compareColor = useColorModeValue('gray.800', 'gray.100');
  const selectedBg = useColorModeValue('green.50', 'green.900');
  const checkColor = useColorModeValue('green.500', 'green.300');

  const { toggleDataset, isSelected, isFull, datasets: comparedDatasets } = useCompare();
  const isCompareEnabled = useIsDatasetComparisonEnabled();
  // Search results are always in the release currently in the URL.
  const routeRef = useRouteDatasetRef();
  const datasetKey = String(dataset.id);
  const selected = isCompareEnabled && isSelected(routeRef(dataset.id));
  const groupSelected = isCompareEnabled && groupDatasetIds.some((id) => isSelected(routeRef(id)));

  const compareItem: CompareDataset = {
    id: datasetKey,
    version: ver,
    systemModel: system_model,
    activityName,
    referenceProduct: referenceProductName,
    geography: dataset.geography,
    unit,
    activityType,
    impactScore: typeof dataset.impact_score === 'number' ? dataset.impact_score : undefined,
    impactUnit: dataset.impact_unit,
    sector: sectors.join(', '),
    timePeriod,
  };

  const isLastVisibleGeography = index === currentlyDisplayed - 1 && isLastGroup;
  const [hovering, setHovering] = React.useState(false);

  const onToggleCompare = () => {
    mixpanel.track(selected ? MIXPANEL_EVENTS.COMPARE_DATASET_REMOVED : MIXPANEL_EVENTS.COMPARE_DATASET_ADDED, {
      dataset_id: datasetKey,
      dataset_version: ver,
      dataset_system_model: system_model,
      source: 'search_activity_row',
      selection_count: selected ? comparedDatasets.length - 1 : comparedDatasets.length + 1,
      geography: dataset.geography,
      result_position: resultPosition,
      dataset_index: index,
    });
    toggleDataset(compareItem);
  };

  return (
    <Tr onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      {isFirstDataset && system_model !== 'undefined' && (
        <Td
          borderBottomWidth={isLastGroup ? 0 : 1}
          verticalAlign={'top'}
          w={{ base: '100%', xl: '350px' }}
          rowSpan={currentlyDisplayed}
          bg={groupSelected ? selectedBg : undefined}
        >
          <Stack spacing={4} py={2}>
            <Heading size="sm">{referenceProductName}</Heading>
            <HStack>
              <Icon as={BiRuler} color={grayText} />
              <Text color={grayText}>{unit}</Text>
            </HStack>
          </Stack>
        </Td>
      )}
      <Td borderBottomWidth={isLastVisibleGeography ? 0 : 1} bg={selected ? selectedBg : undefined}>
        <HStack spacing={2}>
          {selected && <Icon as={IoCheckmarkCircle} color={checkColor} />}
          <Text as="span">{dataset.geography}</Text>
        </HStack>
      </Td>
      {hasScoresAccess && system_model !== 'undefined' && newerVersions.includes(ver) && (
        <Td
          borderColor={borderColor}
          whiteSpace={'nowrap'}
          borderBottomWidth={isLastVisibleGeography ? 0 : 1}
          bg={selected ? selectedBg : undefined}
        >
          {typeof dataset.impact_score === 'number' ? (
            <HStack justifyContent="flex-end">
              <Text fontSize="md">{formatNumericalValue(dataset.impact_score)}</Text>
              <Text color={grayText}>{dataset.impact_unit}</Text>
              <CopyToClipboardButton
                value={dataset.impact_score}
                opacity={hovering ? 1 : 0}
                onValueCopied={() => {
                  mixpanel.track(MIXPANEL_EVENTS.COPY_SCORE_ON_SEARCH_RESULTS, {
                    score: dataset.impact_score,
                    name: referenceProductName,
                    id: dataset.id,
                    geography: dataset.geography,
                  });
                }}
              />
            </HStack>
          ) : (
            <HStack justifyContent="flex-end" pr={14}>
              <Text color={grayText}>N/A</Text>
            </HStack>
          )}
        </Td>
      )}
      <Td borderColor={borderColor} borderBottomWidth={isLastVisibleGeography ? 0 : 1} bg={selected ? selectedBg : undefined}>
        <HStack spacing={4} whiteSpace={'nowrap'}>
          {isCompareEnabled && (
            <Button variant="link" size="sm" color={compareColor} isDisabled={!selected && isFull} onClick={onToggleCompare}>
              Compare
            </Button>
          )}
          <Button
            variant="link"
            alignItems="center"
            as={Link}
            size="sm"
            to={`/${ver}/${system_model}/dataset/${dataset.id}`}
            onClick={() => {
              // Lets the dataset breadcrumb bring the user back to these results
              saveSearchOrigin({ ver, system_model, search, spold_id: dataset.id });
              mixpanel.track(MIXPANEL_EVENTS.SEARCH_RESULT_CLICKED, {
                datasetId: dataset.id,
                query,
                filters: otherFilters,
                result_type: otherFilters.searchBy,
                result_position: resultPosition,
                dataset_index: index,
              });
            }}
          >
            <Text>View Dataset</Text> <Icon as={IoChevronForwardOutline} />
          </Button>
        </HStack>
      </Td>
    </Tr>
  );
};

export default DatasetRow;
